import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Award,
  PlayCircle,
  Clock,
  CheckCircle2,
  CreditCard,
  User,
  ArrowRight,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Enrollment, Certificate, Payment } from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function StudentDashboardPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState<string | null>(null);

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certsLoading, setCertsLoading] = useState(true);
  const [certsError, setCertsError] = useState<string | null>(null);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'courses' | 'certificates' | 'payments'>('courses');

  const fetchCourses = async (userId: string) => {
    setCoursesLoading(true);
    setCoursesError(null);
    try {
      const { data: enrs, error } = await supabase
        .from('enrollments')
        .select('*, course:courses(*)')
        .eq('user_id', userId)
        .order('enrolled_at', { ascending: false });

      if (error) {
        console.error('[STUDENT] Courses fetch error:', error);
        setCoursesError('Failed to load courses.');
      } else {
        setEnrollments(enrs as Enrollment[] || []);
      }
    } catch (err) {
      console.error('[STUDENT] Unexpected error fetching courses:', err);
      setCoursesError('Unable to load courses. Please try again.');
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchCertificates = async (userId: string) => {
    setCertsLoading(true);
    setCertsError(null);
    try {
      const { data: certs, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[STUDENT] Certificates fetch error:', error);
        setCertsError('Failed to load certificates.');
      } else {
        setCertificates(certs as Certificate[] || []);
      }
    } catch (err) {
      console.error('[STUDENT] Unexpected error fetching certificates:', err);
      setCertsError('Unable to load certificates. Please try again.');
    } finally {
      setCertsLoading(false);
    }
  };

  const fetchPayments = async (userId: string) => {
    setPaymentsLoading(true);
    setPaymentsError(null);
    try {
      const { data: pays, error } = await supabase
        .from('payments')
        .select('*')
        .eq('client_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[STUDENT] Payments fetch error:', error);
        setPaymentsError('Failed to load payment history.');
      } else {
        setPayments(pays as Payment[] || []);
      }
    } catch (err) {
      console.error('[STUDENT] Unexpected error fetching payments:', err);
      setPaymentsError('Unable to load payments. Please try again.');
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCourses(user.id);
    fetchCertificates(user.id);
    fetchPayments(user.id);
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-1 pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-darkBlue-900 via-darkBlue-800 to-clairBlue-900 rounded-3xl p-8 sm:p-10 text-white shadow-xl relative overflow-hidden">
            <div className="max-w-2xl relative z-10">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3">
                Student Learning Dashboard
              </span>
              <h1 className="text-3xl sm:text-4xl font-black mb-3">
                Welcome back, {profile?.full_name || 'Learner'}! 👋
              </h1>
              <p className="text-blue-100 text-sm leading-relaxed mb-6">
                Track your active courses, resume where you left off, and access your verified graduation certificates.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/academy"
                  className="px-5 py-2.5 bg-clairBlue-500 hover:bg-clairBlue-400 text-white font-bold text-xs rounded-xl shadow-md transition-colors flex items-center gap-1.5"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Browse Catalog</span>
                </Link>
                <Link
                  to="/profile"
                  className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-semibold text-xs rounded-xl transition-colors"
                >
                  Account Settings
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Enrolled Courses</span>
              <span className="text-3xl font-black text-darkBlue-900">
                {coursesLoading ? (
                  <span className="inline-block w-6 h-6 border-2 border-darkBlue-600 border-t-transparent rounded-full animate-spin" />
                ) : coursesError ? (
                  '-'
                ) : (
                  enrollments.length
                )}
              </span>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Certificates Earned</span>
              <span className="text-3xl font-black text-emerald-600">
                {certsLoading ? (
                  <span className="inline-block w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                ) : certsError ? (
                  '-'
                ) : (
                  certificates.length
                )}
              </span>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Total Completed</span>
              <span className="text-3xl font-black text-amber-500">
                {coursesLoading ? (
                  <span className="inline-block w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  enrollments.filter((e) => e.progress_percent === 100).length
                )}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-3">
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                activeTab === 'courses'
                  ? 'bg-darkBlue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              My Courses {coursesLoading ? '(…)' : `(${enrollments.length})`}
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                activeTab === 'certificates'
                  ? 'bg-darkBlue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Certificates {certsLoading ? '(…)' : `(${certificates.length})`}
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                activeTab === 'payments'
                  ? 'bg-darkBlue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Payment History {paymentsLoading ? '(…)' : `(${payments.length})`}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'courses' ? (
            coursesLoading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-xs text-gray-500">Loading your enrolled courses...</p>
              </div>
            ) : coursesError ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-red-100 p-8">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-gray-800 mb-1">Failed to load courses</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">{coursesError}</p>
                <button
                  onClick={() => user && fetchCourses(user.id)}
                  className="px-4 py-2 bg-darkBlue-600 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-700"
                >
                  Try Again
                </button>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">No enrolled courses yet</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-6">
                  Explore our practical courses in Microsoft Office, AI tools, freelancing, and career development.
                </p>
                <Link
                  to="/academy"
                  className="px-6 py-2.5 bg-darkBlue-600 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-700"
                >
                  Explore Course Catalog
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrollments.map((enr) => (
                  <div
                    key={enr.id}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between hover:shadow-md transition-shadow"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-clairBlue-600 bg-clairBlue-50 px-2.5 py-0.5 rounded-full">
                          {enr.status}
                        </span>
                        <span className="text-xs text-gray-400 font-semibold">
                          {enr.progress_percent}%
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">
                        {enr.course?.title || 'Course'}
                      </h3>

                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden my-4">
                        <div
                          className="h-full bg-gradient-to-r from-darkBlue-600 to-clairBlue-500 rounded-full transition-all"
                          style={{ width: `${enr.progress_percent}%` }}
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {enr.progress_percent === 100 ? 'Completed' : 'In Progress'}
                      </span>
                      {enr.course && (
                        <Link
                          to={`/academy/learn/${enr.course.slug}`}
                          className="px-4 py-2 bg-darkBlue-600 hover:bg-darkBlue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                        >
                          <PlayCircle className="w-4 h-4" />
                          <span>{enr.progress_percent === 0 ? 'Start' : 'Resume'}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'certificates' ? (
            certsLoading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-xs text-gray-500">Loading your certificates...</p>
              </div>
            ) : certsError ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-red-100 p-8">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-gray-800 mb-1">Failed to load certificates</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">{certsError}</p>
                <button
                  onClick={() => user && fetchCertificates(user.id)}
                  className="px-4 py-2 bg-darkBlue-600 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-700"
                >
                  Try Again
                </button>
              </div>
            ) : certificates.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">No certificates issued yet</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Complete all modules in an enrolled course and pass the final assessment quiz to earn your official certificate.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        <span className="text-xs font-bold text-gray-400 font-mono">
                          {cert.certificate_code}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-2">
                        {cert.course_title}
                      </h3>
                      <p className="text-xs text-gray-500 mb-4">
                        Issued on{' '}
                        {new Date(cert.issue_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>

                    <Link
                      to={`/verify-certificate/${cert.certificate_code}`}
                      className="w-full py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-darkBlue-700 font-bold text-xs rounded-xl text-center transition-colors block"
                    >
                      View & Print Certificate
                    </Link>
                  </div>
                ))}
              </div>
            )
          ) : (
            paymentsLoading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-xs text-gray-500">Loading payment history...</p>
              </div>
            ) : paymentsError ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-red-100 p-8">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <h3 className="text-base font-bold text-gray-800 mb-1">Failed to load payments</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">{paymentsError}</p>
                <button
                  onClick={() => user && fetchPayments(user.id)}
                  className="px-4 py-2 bg-darkBlue-600 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-700"
                >
                  Try Again
                </button>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">No transaction records</h3>
                <p className="text-xs text-gray-500">Your verified payment receipts will appear here.</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[550px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">Reference</th>
                        <th className="px-6 py-4">Provider / Method</th>
                        <th className="px-6 py-4">Amount</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {payments.map((pay) => (
                        <tr key={pay.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 font-mono font-bold text-darkBlue-900">
                            {pay.payment_reference}
                          </td>
                          <td className="px-6 py-4">{pay.provider}</td>
                          <td className="px-6 py-4 font-bold text-emerald-600">
                            ${pay.amount.toFixed(2)} {pay.currency}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {pay.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-400">
                            {new Date(pay.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
